import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import MapPin from "@/images/uiMapPin.png";
import uiMapPinActive from "@/images/uiMapPinActive.png";
import uiMapLocaltion from "@/images/uiMapLocaltion.png";
import { useDebounceFn, useDebounceEffect } from "ahooks";

import "./index.less";

const isDev = window.location.hostname === "localhost";

/* global L */

// ZIndexOffset: 888 点击选中
// ZIndexOffset：999 鼠标悬停

/**
 * 地图组件
 * 自动判定选择谷歌或高德地图
 *
 * @param {any} restaurants - 餐厅数据列表
 * @param {any} pageCity - 区域（含国家）
 * @param {any} pos - 浏览器定位位置
 * @param {any} mapRange - 上次设置的地图范围
 * @param {Function} tapMarkers - 点选标记回调
 * @param {Function} onMapChange - 地图变动回调
 * @param {Function} onHomeVisibleChange - 定位点是否可见回调
 * @param {boolean} autoFitBoundsRef - 是否自动缩放地图到所有标记（默认 true）
 * @returns 地图组件
 */
const ExploreMap = forwardRef(
  (
    {
      restaurants,
      pageCity,
      pos,
      mapRange,
      tapMarkers,
      onMapChange,
      onHomeVisibleChange,
      autoFitBoundsRef,
      lockRef,
    },
    ref,
  ) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const tileLayerRef = useRef(null);
    const markerLayerRef = useRef(null);
    const currentBoundsRef = useRef(null); // 用于保存初始化的 bounds
    const skipNextMapChangeCountRef = useRef(0);
    const markersRef = useRef([]);
    const selectedClusterIdRef = useRef(null);
    const tapMarkersRef = useRef(tapMarkers);
    const onMapChangeRef = useRef(onMapChange);
    const posMarkerRef = useRef(null);
    const isResizingRef = useRef(null);
    const isAnimatingRef = useRef(false);
    const [hoverMarkerId, setHoverMarkerId] = useState(null);

    // 监听定位标记是否在可视范围内变化
    const onPosVisibleChange = (map) => {
      if (posMarkerRef.current) {
        onHomeVisibleChange(false);
      }
    };
    const { run: triggerMapChange } = useDebounceFn(
      (mapInstance) => {
        if (typeof onMapChangeRef.current === "function") {
          const bounds = mapInstance.getBounds();
          onMapChangeRef.current({
            zoom: mapInstance.getZoom(),
            bounds,
          });
        }
      },
      { wait: 200 },
    );

    const clearSelectedCluster = () => {
      if (selectedClusterIdRef.current == null) return;
      selectedClusterIdRef.current = null;
      if (markerLayerRef.current?.refreshClusters) {
        markerLayerRef.current.refreshClusters();
      }
    };

    const clearSelected = () => {
      if (markersRef.current) {
        markersRef.current.forEach((layer) => {
          layer.setIcon(L.icon({ iconSize: [52, 60], iconUrl: MapPin }));
          layer.setZIndexOffset(0);
        });
      }
    };

    // hover需要等待500ms，避免频繁触发
    useDebounceEffect(
      () => {
        if (!hoverMarkerId) return;
        const isSelected = hoverMarkerId.marker.options.zIndexOffset == 888;
        if (isSelected) return;
        mapRef.current.closePopup();
        clearSelected();
        clearSelectedCluster();
        hoverMarkerId.marker.setIcon(
          L.icon({ iconSize: [52, 60], iconUrl: uiMapPinActive }),
        );
        hoverMarkerId.marker.setZIndexOffset(999);
        tapMarkersRef.current?.(
          [hoverMarkerId.id],
          mapRef.current?.getBounds(),
          hoverMarkerId.latlng,
        );
      },
      [hoverMarkerId],
      { wait: 100 },
    );

    useEffect(() => {
      tapMarkersRef.current = tapMarkers;
    }, [tapMarkers]);

    useEffect(() => {
      onMapChangeRef.current = onMapChange;
    }, [onMapChange]);

    // 监听窗口 resize 事件
    useEffect(() => {
      let resizeTimer = null;

      const handleResize = () => {
        isResizingRef.current = true;

        // 清除之前的定时器
        if (resizeTimer) clearTimeout(resizeTimer);

        // resize 结束后恢复
        resizeTimer = setTimeout(() => {
          isResizingRef.current = false;
        }, 300);
      };

      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        if (resizeTimer) clearTimeout(resizeTimer);
      };
    }, []);

    // 初始化地图
    useEffect(() => {
      if (!pageCity) return;
      if (!pageCity.country_name) return;

      const countryName = String(pageCity?.country_name || "").toLowerCase();
      const inChina = countryName === "china";

      const normal = L.layerGroup([
        L.tileLayer(
          "https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_en&size=1&scale=1&style=8&x={x}&y={y}&z={z}",
          {
            maxZoom: 18,
            minZoom: 5,
            subdomains: "1234",
          },
        ),
      ]);

      let googleMap = null;
      if (countryName === "hongkong") {
        googleMap = L.tileLayer.chinaProvider("Google.Normal.Map", {
          maxZoom: 18,
          minZoom: 5,
        });
      } else {
        googleMap = L.tileLayer(
          `https://{s}.google.com/vt?lyrs=m&x={x}&y={y}&z={z}&hl=${pageCity.country_name}`,
          {
            maxZoom: 18,
            minZoom: 5,
            subdomains: ["mt0", "mt1", "mt2", "mt3"],
          },
        );
      }
      var styledGoogleMap = L.gridLayer.googleMutant({
        maxZoom: 18,
        minZoom: 5,
        type: "roadmap",
        mapId: "906d400834346f0546a53818",
      });

      // console.log("styledGoogleMap", styledGoogleMap);

      const google = L.layerGroup([styledGoogleMap]);
      // const google = L.layerGroup([googleMap]);

      const targetLayer = inChina ? normal : google;

      if (mapRef.current) {
        // 地图已存在，切换图层
        if (tileLayerRef.current) {
          mapRef.current.removeLayer(tileLayerRef.current);
        }
        targetLayer.addTo(mapRef.current);
        tileLayerRef.current = targetLayer;
        return;
      }

      {
        console.error("Initializing map with city:", pos?.lat, pos?.lng);
        const mapCenter = pos?.lat
          ? [pos.lat, pos.lng]
          : [pageCity.lat, pageCity.lng];
        const map = L.map("explore-map", {
          center: mapCenter,
          zoom: pos?.lat ? 15 : 13,
          layers: [targetLayer],
          zoomControl: false,
          attributionControl: false,
        });
        tileLayerRef.current = targetLayer;

        if (pos?.lat && pos?.lng) {
          triggerMapChange(map);
        }

        map.on("click", (evt) => {
          window._lastMapClickLatLng = evt.latlng; // 记录最后一次点击的坐标，供外部调用
          map.closePopup();
          tapMarkersRef.current?.([]);
          // 所有标记恢复默认样式
          clearSelected();
          clearSelectedCluster();
        });

        map.on("movestart", () => {
          isAnimatingRef.current = true;
          map.closePopup();
        });

        map.on("moveend", () => {
          isAnimatingRef.current = false;
          if (isResizingRef.current) return;
          onPosVisibleChange(map);
          if (skipNextMapChangeCountRef.current > 0) {
            skipNextMapChangeCountRef.current--;
            return;
          }
          triggerMapChange(map);
        });
        mapRef.current = map;
      }
    }, [pageCity, triggerMapChange]);

    // 更新地图标记
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !pageCity) return;
      markersRef.current = [];
      selectedClusterIdRef.current = null;
      setHoverMarkerId(null);
      if (!restaurants || restaurants.length === 0) {
        // pageCity 的 lat/lng 作为 中心点
        const centerLat = Number(pageCity.lat);
        const centerLng = Number(pageCity.lng);
        if (
          !isNaN(centerLat) &&
          !isNaN(centerLng) &&
          !markerLayerRef.current &&
          !mapRange
        ) {
          skipNextMapChangeCountRef.current = 1; // 防止 moveend 被触发
          map.setView([centerLat, centerLng], 10); // 设置默认中心和缩放级别
        }
        // 需要清空地图标记
        if (markerLayerRef.current) {
          map.removeLayer(markerLayerRef.current);
          markerLayerRef.current = null;
        }
        return;
      }

      if (markerLayerRef.current) {
        map.removeLayer(markerLayerRef.current);
      }

      const markers = L.markerClusterGroup({
        spiderfyOnMaxZoom: false,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        maxClusterRadius: 40,
        animateAddingMarkers: false,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          const isActive = selectedClusterIdRef.current === cluster._leaflet_id;
          return L.divIcon({
            className: `explore-cluster-marker${isActive ? " is-active" : ""}`,
            html: `<b>${count > 99 ? "99+" : count}</b>`,
            iconSize: [52, 60],
            iconAnchor: [26, 50],
          });
        },
      });

      const totalLatLng = {
        maxLat: -99999,
        minLat: 99999,
        maxLng: -99999,
        minLng: 99999,
      };

      restaurants.forEach((item, index) => {
        let { lat, lon } = item.lat_lon;
        lat = Number(lat) || pageCity.lat;
        lon = Number(lon) || pageCity.lng;

        if (lat && lon) {
          totalLatLng.maxLat = Math.max(lat, totalLatLng.maxLat);
          totalLatLng.minLat = Math.min(lat, totalLatLng.minLat);
          totalLatLng.maxLng = Math.max(lon, totalLatLng.maxLng);
          totalLatLng.minLng = Math.min(lon, totalLatLng.minLng);
        }

        const position = new L.LatLng(lat, lon);
        const marker = L.marker(position, {
          marker_id: item.id,
          icon: L.icon({
            iconSize: [52, 60],
            iconAnchor: [26, 30],
            iconUrl: MapPin,
          }),
        });

        // 鼠标移入/移出仅在桌面端生效，移动端不监听
        const isMobile = window.innerWidth <= 768;
        if (!isMobile) {
          // 鼠标移入，展示popup
          marker.on("mouseover", (evt) => {
            if (lockRef.current) return;
            // 地图动画中不触发 hover
            if (isAnimatingRef.current) return;
            // 判断是否是激活状态
            const isSelected = evt.target.options.zIndexOffset == 888;
            if (isSelected) return;
            setHoverMarkerId({
              id: evt.target.options.marker_id,
              latlng: evt.target.getLatLng(),
              marker: evt.target,
            });
          });

          // 鼠标移出，关闭popup
          marker.on("mouseout", (evt) => {
            if (lockRef.current) return;
            // 判断是否是激活状态
            const offset = evt.target.options.zIndexOffset;
            // 点击选中的，不处理
            if (offset == 888) {
              return;
            }
            if (offset == 999) {
              evt.target.setIcon(
                L.icon({ iconSize: [52, 60], iconUrl: MapPin }),
              );
              evt.target.setZIndexOffset(0);
              mapRef.current.closePopup();
            }
            setHoverMarkerId(null);
          });
        }

        marker.on("click", (evt) => {
          if (lockRef.current) return;
          // 已选中则不处理
          if (evt.target.options.zIndexOffset == 999) {
            evt.target.options.zIndexOffset = 888;
            return;
          }
          tapMarkersRef.current?.(
            [item.id],
            mapRef.current?.getBounds(),
            marker.getLatLng(),
          );
          clearSelected();
          clearSelectedCluster();
          marker.setIcon(
            L.icon({ iconSize: [52, 60], iconUrl: uiMapPinActive }),
          );
          marker.setZIndexOffset(888);
        });
        markers.addLayer(marker);
        markersRef.current.push(marker);
      });

      markers.on("clusterclick", (a) => {
        if (lockRef.current) return;
        clearSelected();

        // 判断聚合是否可以继续展开：
        const childMarkers = a.layer.getAllChildMarkers();
        const canExpand = (() => {
          if (map.getZoom() >= map.getMaxZoom()) return false;
          if (childMarkers.length <= 1) return false;
          // 检查聚合边界是否超过地图边界
          var latLngBounds = a.layer.getBounds();
          return map.getBounds().contains(latLngBounds);
        })();

        if (canExpand) {
          selectedClusterIdRef.current = null;
          markers.refreshClusters();
          // skipNextMapChangeCountRef.current = 1; // 跳过接下来的 moveend
          a.layer.zoomToBounds({ padding: [20, 20] });
        } else {
          // 无法继续展开，选中该聚合点
          const childMarkers = a.layer.getAllChildMarkers();
          const markersIds = childMarkers.map((m) => m.options.marker_id);

          // 触发 iconCreateFunction 重跑，达到“重绘该聚合点”的效果
          selectedClusterIdRef.current = a.layer._leaflet_id;
          markers.refreshClusters();

          tapMarkersRef.current?.(
            markersIds,
            mapRef.current?.getBounds(),
            a.layer.getLatLng(),
          );
        }
      });

      // 使用 ref 同步判断，避免 React state 异步更新导致的闭包问题
      const shouldFitBounds = autoFitBoundsRef
        ? autoFitBoundsRef.current
        : true;
      if (shouldFitBounds && totalLatLng.maxLat !== -99999) {
        const bounds = [
          [totalLatLng.minLat, totalLatLng.minLng],
          [totalLatLng.maxLat, totalLatLng.maxLng],
        ];
        skipNextMapChangeCountRef.current = 1;
        map.fitBounds(bounds, { padding: [20, 20] });
        currentBoundsRef.current = bounds;
      }

      map.addLayer(markers);
      markerLayerRef.current = markers;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageCity, restaurants]); // 移除 autoFitBounds 依赖，改用 ref 同步判断

    // 绘制当前位置标记
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !pos) return;

      const lat = Number(pos.lat);
      const lng = Number(pos.lng || pos.lon);
      if (isNaN(lat) || isNaN(lng)) return;

      // 如果标记已存在，更新位置
      if (posMarkerRef.current) {
        posMarkerRef.current.setLatLng([lat, lng]);
        return;
      }

      // 创建带呼吸动画的 div 图标
      const locationIcon = L.divIcon({
        className: "explore-location-marker",
        html: `<div class="location-pulse"></div><img src="${uiMapLocaltion}" alt="location" />`,
        iconSize: [50, 50],
        iconAnchor: [25, 25],
      });

      const marker = L.marker([lat, lng], {
        icon: locationIcon,
        zIndexOffset: 9999,
      });

      marker.addTo(map);
      posMarkerRef.current = marker;
      onPosVisibleChange(map);
    }, [pos, pageCity]);

    // 对外暴露重置视图方法
    useImperativeHandle(ref, () => ({
      resetView: () => {
        const map = mapRef.current;
        const bounds = currentBoundsRef.current;
        if (map && bounds) {
          map.fitBounds(bounds, { padding: [20, 20] });
        }
      },
      nearView: (pos) => {
        const map = mapRef.current;
        if (!map || !pos) return;
        const latLng = L.latLng(pos.lat, pos.lng);
        // // toBounds 参数为直径（米），半径5公里 → 直径10公里
        // const circleBounds = latLng.toBounds(5000);
        // skipNextMapChangeCountRef.current = 1;
        // map.fitBounds(circleBounds, { animate: false });
        // map.zoomIn(); // 微调放大一级，确保覆盖整个圆形区域
        map.setView(latLng, 15, { animate: true });
      },
      // 定位到当前位置
      backHome: (force) => {
        const map = mapRef.current;
        const posMarker = posMarkerRef.current;
        console.error("Back to current location:", force, pos?.lat, pos?.lng);
        if (map && posMarker) {
          const latLng = posMarker.getLatLng();
          // 如果 posMarker 已在地图中心且缩放级别一致，忽略本次调用
          const center = map.getCenter();
          const zoom = map.getZoom();
          const threshold = 1e-5; // 约1米精度
          if (force) {
            map.setView(latLng, 15, { animate: true });
            return;
          }
          if (
            zoom === 15 &&
            Math.abs(center.lat - latLng.lat) < threshold &&
            Math.abs(center.lng - latLng.lng) < threshold
          ) {
            return;
          }
          map.setView(latLng, 15, { animate: true });
        }
      },
      // 高亮指定标记
      highlightMarker: (markerId, move = true) => {
        clearSelected();
        clearSelectedCluster();
        selectedClusterIdRef.current = null;

        const map = mapRef.current;
        const targetMarker = markersRef.current.find(
          (m) => m.options.marker_id == markerId,
        );

        if (!targetMarker || !map) return;

        const clusterGroup = markerLayerRef.current;
        const parent = clusterGroup?.getVisibleParent
          ? clusterGroup.getVisibleParent(targetMarker)
          : null;

        // 使用 project/unproject 计算偏移中心（基于绝对像素坐标，不依赖当前视口，更可靠）
        const panToOffsetCenter = (latLng) => {
          // 停止当前正在进行的动画，避免与新 panTo 冲突
          const zoom = map.getZoom();
          // project 将 latLng 转为当前缩放级别下的绝对像素坐标
          const targetPixel = map.project(latLng, zoom);
          // 向下偏移 50px，使标记出现在地图中心偏上 50px 的位置
          const newCenterPixel = L.point(targetPixel.x, targetPixel.y + 100);
          const newCenter = map.unproject(newCenterPixel, zoom);
          skipNextMapChangeCountRef.current = 1;

          // 计算当前中心与目标中心的像素距离，距离过大时不使用动画以确保到位
          const currentCenterPixel = map.project(map.getCenter(), zoom);
          const mapSize = map.getSize();
          const pixelDistance = currentCenterPixel.distanceTo(newCenterPixel);
          const maxAnimateDistance = Math.max(mapSize.x, mapSize.y) * 2.5;
          const shouldAnimate = pixelDistance < maxAnimateDistance;

          map.panTo(newCenter, {
            animate: shouldAnimate,
            duration: 0.3,
          });
        };

        if (parent && parent !== targetMarker) {
          selectedClusterIdRef.current = parent._leaflet_id;
          clusterGroup?.refreshClusters?.();
          if (move) {
            // 将聚合点移动到偏上50px的位置
            const clusterLatLng = parent.getLatLng();
            panToOffsetCenter(clusterLatLng);
          }
          return;
        }

        targetMarker.setIcon(
          L.icon({ iconSize: [52, 60], iconUrl: uiMapPinActive }),
        );
        targetMarker.setZIndexOffset(888);
        // 将标记移动到偏上50px的位置
        const markerLatLng = targetMarker.getLatLng();
        if (move) {
          panToOffsetCenter(markerLatLng);
        }
      },
      // 高亮指定标记
      hoverMarker: (markerId, latlng) => {
        clearSelected();
        clearSelectedCluster();
        selectedClusterIdRef.current = null;

        const map = mapRef.current;
        const targetMarker = markersRef.current.find(
          (m) => m.options.marker_id == markerId,
        );

        if (!targetMarker || !map) return;

        const clusterGroup = markerLayerRef.current;
        const parent = clusterGroup?.getVisibleParent
          ? clusterGroup.getVisibleParent(targetMarker)
          : null;

        if (parent && parent !== targetMarker) {
          selectedClusterIdRef.current = parent._leaflet_id;
          clusterGroup?.refreshClusters?.();
        } else {
          targetMarker.setIcon(
            L.icon({ iconSize: [52, 60], iconUrl: uiMapPinActive }),
          );
          targetMarker.setZIndexOffset(999);
        }
      },
      zoomIn: () => {
        const map = mapRef.current;
        if (map) {
          // 最大层级？
          const maxZoom = map.getMaxZoom();
          if (map.getZoom() < maxZoom) {
            map.zoomIn();
          }
        }
      },
      zoomOut: () => {
        const map = mapRef.current;
        if (map) {
          const minZoom = map.getMinZoom();
          if (map.getZoom() > minZoom) {
            map.zoomOut();
          }
        }
      },
      closePopup: () => {
        const map = mapRef.current;
        if (map) map.closePopup();
      },
      setPopup: (latLng, content) => {
        L.responsivePopup({
          closeOnClick: false,
          autoPan: true,
          closeButton: false,
          offset: L.point(0, 30),
          autoPanPadding: [20, 20],
          minWidth: 380,
          className: "explore-map-popup",
        })
          .setLatLng(latLng)
          .setContent(content)
          .openOn(mapRef.current);
      },
    }));

    return (
      <div
        ref={containerRef}
        className="explore-map-view"
        id="explore-map"
      ></div>
    );
  },
);

export default ExploreMap;
